import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./WalletPage.css";

export default function WalletPage() {
  const navigate = useNavigate();
  const [balance, setBalance] = useState(0);
  const [checkinData, setCheckinData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);

  const reward = 10;
  const days = [1, 2, 3, 4, 5, 6, 7];
  const user = JSON.parse(localStorage.getItem("user"));

  // ✅ Normalisasi data dari API agar boolean sesuai
  const normalizeCheckinData = (data) => {
    const normalized = { ...data };
    for (let i = 1; i <= 7; i++) {
      normalized[`day${i}`] =
        data[`day${i}`] === true ||
        data[`day${i}`] === 1 ||
        data[`day${i}`] === "1";
    }
    normalized.total_profit = parseFloat(data.total_profit || 0);
    return normalized;
  };

  // ✅ Fetch semua data (balance, checkin, transaksi)
  const fetchData = useCallback(async () => {
    if (!user) return;
    try {
      const [walletRes, checkinRes, txRes] = await Promise.all([
        fetch(`https://api.backofficemain.com/get_wallet.php?username=${user.username}`),
        fetch("https://api.backofficemain.com/get_checkin.php", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({ username: user.username }),
        }),
        fetch(`https://api.backofficemain.com/get_transactions.php?username=${user.username}`)
      ]);

      //const walletData = await walletRes.json();
      const checkinResData = await checkinRes.json();
      const txData = await txRes.json();
      const walletRaw = await walletRes.text();
      console.log("walletRaw:", walletRaw);
      const walletData = JSON.parse(walletRaw);

      const validCheckin =
        checkinResData && typeof checkinResData === "object"
          ? normalizeCheckinData(checkinResData)
          : { total_profit: 0 };

      setBalance(parseFloat(walletData.balance || 0));
      setCheckinData(validCheckin);
      setTransactions(Array.isArray(txData) ? txData : []);
    } catch (err) {
      console.error("Error fetching wallet data:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ✅ Handle Check-in Harian
  const handleCheckIn = async () => {
    if (!user || checkingIn) return;
    setCheckingIn(true);

    try {
      const res = await fetch("https://api.backofficemain.com/daily_checkin.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ username: user.username }),
      });
      const data = await res.json();

      if (data.status === "checked") {
        alert("You’ve already checked in today!");
      } else if (data.status === "success") {
        alert(`✅ Checked in successfully! +$${reward}`);
        setBalance((prev) => prev + reward);
        setCheckinData(normalizeCheckinData(data.data));
      } else {
        alert(data.message || "Check-in failed.");
      }
    } catch (err) {
      console.error("Check-in error:", err);
    } finally {
      setCheckingIn(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5 text-muted">
        Loading wallet data...
      </div>
    );
  }

  const totalBonus = parseFloat(checkinData?.total_profit || 0);
  const alreadyChecked =
    checkinData?.last_checkin === new Date().toISOString().slice(0, 10);

  return (
    <div className="container py-4">
      {/* Wallet Balance */}
      <div className="card text-center bg-danger text-white shadow mb-4">
        <div className="card-body">
          <h6 className="mb-1">Wallet Balance</h6>
          <h2 className="fw-bold text-white">
            ${Number(balance || 0).toFixed(2)}
          </h2>
          <div className="d-flex justify-content-center mt-3">
            <button
              className="btn btn-light mx-2 fw-bold"
              onClick={() => navigate("/deposit")}
            >
              Deposit
            </button>
            <button
              className="btn btn-light mx-2 fw-bold"
              onClick={() => navigate("/withdraw")}
            >
              Withdraw
            </button>
          </div>
        </div>
      </div>

      {/* ✅ Daily Check-in */}
      <div className="card shadow mb-4">
        <div className="card-body text-center">
          <h6 className="fw-bold text-danger">Daily bonus of ${reward}</h6>
          <div className="d-flex justify-content-center gap-3 my-3 flex-wrap">
            {days.map((day) => {
              const isChecked = checkinData?.[`day${day}`] === true;
              return (
                <div key={day} className="text-center">
                  <div
                    className={`circle ${isChecked
                        ? "bg-primary text-white"
                        : "border-secondary"
                      }`}
                  >
                    {isChecked ? "✓" : day}
                  </div>
                  <small className="text-muted">Day {day}</small>
                </div>
              );
            })}
          </div>

          <p className="fw-bold mt-2 text-dark">
            Total Bonus: ${Number(totalBonus || 0).toFixed(2)}
          </p>

          {alreadyChecked ? (
            <div className="alert alert-success py-2 m-0">
              ✅ You’ve already checked in today
            </div>
          ) : (
            <button
              className="btn btn-danger rounded-pill px-4 mt-2"
              onClick={handleCheckIn}
              disabled={checkingIn}
            >
              {checkingIn ? "Processing..." : "Check in"}
            </button>
          )}
        </div>
      </div>

      {/* Transaction History */}
      <div className="card shadow">
        <div className="card-body">
          <h5 className="fw-bold text-danger mb-3">
            Recent Transaction History
          </h5>

          {transactions.length === 0 ? (
            <div className="text-center py-3 text-muted">
              No transactions found.
            </div>
          ) : (
            transactions.map((item, index) => (
              <div
                key={index}
                className="d-flex justify-content-between align-items-center border-bottom py-2"
              >
                <div>
                  <strong>{item.title}</strong>
                  <div className="text-muted small">{item.date}</div>
                </div>
                <div
                  className={`fw-bold ${item.amount < 0 ? "text-danger" : "text-success"
                    }`}
                >
                  {item.amount < 0
                    ? `-$${Math.abs(item.amount)}`
                    : `+$${item.amount}`}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
